import { useCallback, useMemo, useState } from 'react';

import { useFeatureFlagVersion } from 'context/FeatureFlagContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Tab, Tabs } from 'ui-kit';

import { ActiveTradesContext } from './ActiveTradesContext';
import AllTradesTab from './AllTradesTab';
import BidsTab from './BidsTab';
import ComparisonViewTab from './ComparisonViewTab';

const TABS = ['all', 'bids', 'comparison'] as const;
type TabKey = (typeof TABS)[number];

const getTabIndex = (tab: TabKey) => TABS.indexOf(tab);
const getTabKey = (index: number): TabKey => TABS[index] ?? 'all';

const ActiveTrades = () => {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: TabKey }>();
  const version = useFeatureFlagVersion('bidsTable');

  const activeTab = useMemo(() => getTabIndex(tab ?? 'all'), [tab]);

  const handleTabChange = (index: number) => {
    const newTab = getTabKey(index);
    if (newTab === 'comparison' && !!selectedListingIds.length) {
      const queryString = `?ids=${selectedListingIds.join(',')}`;
      navigate(`/marketplace/active_trades/${newTab}${queryString}`);
    } else {
      navigate(`/marketplace/active_trades/${newTab}`);
    }
  };

  const [selectedListingIds, setSelectedListingIds] = useState<string[]>([]);

  const addSelectedListingId = useCallback((id: string) => {
    setSelectedListingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const removeSelectedListingId = useCallback((id: string) => {
    setSelectedListingIds((prev) => prev.filter((existingId) => existingId !== id));
  }, []);

  const clearSelectedListingIds = useCallback(() => {
    setSelectedListingIds([]);
  }, []);

  const renderContent = () => {
    if (version === 'v1') {
      return (
        <Tabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabListClassName="flex justify-center bg-slate-950 pt-6 pb-8 rounded-t-md"
          tabPanelClassName="px-4"
          className="rounded-md bg-slate-950"
        >
          <Tab label="All Trades">
            <AllTradesTab />
          </Tab>
          <Tab label="Bids">
            <BidsTab />
          </Tab>
          <Tab label="Comparison View">
            <ComparisonViewTab />
          </Tab>
        </Tabs>
      );
    }

    return <AllTradesTab />;
  };

  return (
    <ActiveTradesContext.Provider
      value={{
        selectedListingIds,
        setSelectedListingIds,
        addSelectedListingId,
        removeSelectedListingId,
        clearSelectedListingIds,
      }}
    >
      <div className="w-screen px-6 pt-9 text-slate-200">{renderContent()}</div>
    </ActiveTradesContext.Provider>
  );
};

export default ActiveTrades;
